"use client";

import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";

import { fetchJson } from "@/lib/api";

type Suggestion = {
  id: number | string;
  title?: string;
  body?: string;
  status?: string;
  payload_json?: string;
};

export type AssistantContext = {
  scope: "all" | string;
  section: "home" | "chat" | "calendar" | "files" | "me";
  pathname: string;
  searchParams: Record<string, string>;
  selectedWorkspace?: {
    slug: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
    role?: string;
    avatarUrl?: string | null;
  };
};

type AssistantActionType =
  | "CREATE_EVENT"
  | "UPDATE_EVENT"
  | "CREATE_ATTENDANCE_PLAN"
  | "CREATE_ATTENDANCE_RECORD"
  | "ANSWER_SURVEY"
  | "SEND_DM"
  | "POST_CHANNEL_MESSAGE"
  | "CREATE_DOCUMENT"
  | "UPDATE_DOCUMENT"
  | "CREATE_TODO"
  | "UPDATE_NOTIFICATION_SETTING"
  | "SEND_FEEDBACK_TO_DEVELOPER";

const actionRisk: Record<AssistantActionType, "normal" | "double"> = {
  CREATE_EVENT: "normal",
  UPDATE_EVENT: "normal",
  CREATE_ATTENDANCE_PLAN: "normal",
  CREATE_ATTENDANCE_RECORD: "normal",
  ANSWER_SURVEY: "normal",
  SEND_DM: "double",
  POST_CHANNEL_MESSAGE: "double",
  CREATE_DOCUMENT: "normal",
  UPDATE_DOCUMENT: "normal",
  CREATE_TODO: "normal",
  UPDATE_NOTIFICATION_SETTING: "normal",
  SEND_FEEDBACK_TO_DEVELOPER: "normal",
};

export function AiFloatingAssistant({
  workspaceSlug,
  workspaceOptions = [],
  context,
  mode = "floating",
}: {
  workspaceSlug?: string | null;
  workspaceOptions?: Array<{ slug: string; name: string }>;
  context: AssistantContext;
  mode?: "floating" | "embedded";
}) {
  const pathname = usePathname();
  const router = useRouter();
  const [targetWorkspace, setTargetWorkspace] = useState("");
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const embedded = mode === "embedded";
  const jobType = embedded ? "workspace_question" : context.section === "files" ? "document_draft" : context.section === "calendar" ? "calendar_brief" : context.section === "chat" ? "channel_summary" : "issue_suggestion_from_channel";
  const selectedWorkspace = workspaceSlug ?? targetWorkspace;
  const allScope = context.scope === "all";

  async function run() {
    if (!selectedWorkspace) {
      setMessage("全体scopeでは、実行先workspaceを選んでから提案を作成してください。");
      return;
    }
    setLoading(true);
    setMessage("");
    setSuggestions([]);
    try {
      const contextSummary = buildContextSummary({
        assistantContext: {
          ...context,
          selectedWorkspace: workspaceOptions.find((workspace) => workspace.slug === selectedWorkspace),
        },
        workspaceSlug: selectedWorkspace,
        workspaceOptions,
      });
      const data = await fetchJson<{ suggestions: Suggestion[] }>(`/api/workspaces/${selectedWorkspace}/ai/jobs`, {
        method: "POST",
        body: JSON.stringify({
          job_type: jobType,
          text: prompt || `${context.scope}/${context.section} の状況を整理してください`,
          route: pathname,
          scope: context.scope,
          section: context.section,
          target_workspace_slug: selectedWorkspace,
          context: contextSummary,
          assistant_context: context,
          action_types: Object.keys(actionRisk),
        }),
      });
      setSuggestions(data.suggestions ?? []);
      setPrompt("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "AI提案に失敗しました");
    } finally {
      setLoading(false);
    }
  }

  async function decide(suggestionId: string | number, decision: "approve" | "reject") {
    if (!selectedWorkspace) return;
    const data = await fetchJson<{ suggestions: Suggestion[]; materialized?: unknown }>(`/api/workspaces/${selectedWorkspace}/ai/suggestions/${String(suggestionId)}/${decision}`, {
      method: "POST",
      body: JSON.stringify({ route: pathname, scope: context.scope, section: context.section, target_workspace_slug: selectedWorkspace, assistant_context: context }),
    });
    setSuggestions(data.suggestions ?? []);
    setMessage(decision === "approve" ? "実行しました。対象画面を更新しています。" : "却下理由を監査ログへ保存しました。");
    router.refresh();
  }

  return (
    <div className={embedded ? "h-full min-h-0" : "fixed bottom-24 right-4 z-30 md:bottom-6 md:right-6"}>
      {embedded || open ? (
        <aside
          data-testid="ai-assistant-drawer"
          className={embedded
            ? "grid h-full min-h-0 gap-3 overflow-y-auto p-4"
            : "mb-3 grid max-h-[72dvh] w-[min(420px,calc(100vw-2rem))] gap-3 overflow-y-auto rounded-md border border-[color:var(--line)] bg-white p-4 shadow-[0_20px_60px_rgba(18,18,18,0.18)]"}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase text-[color:var(--muted)]">AI Assistant</div>
              <h2 className="mt-1 font-semibold">{embedded ? "ワークスペースに質問" : "現在の画面をもとに提案"}</h2>
              <p data-testid="ai-assistant-context" className="mt-1 text-xs text-[color:var(--muted)]">
                scope: {context.scope} / section: {context.section} / target: {selectedWorkspace || "未選択"} / user: {context.user.name}
              </p>
            </div>
            {!embedded ? <button type="button" className="icon-button h-8 w-8 rounded-md text-sm" onClick={() => setOpen(false)} aria-label="AIを閉じる">x</button> : null}
          </div>
          {allScope ? (
            <label className="grid gap-1 text-xs font-semibold text-[color:var(--muted)]">
              実行先workspace
              <select data-testid="ai-target-workspace" className="field-select h-10 rounded-md text-sm font-normal text-[color:var(--fg)]" value={targetWorkspace} onChange={(event) => setTargetWorkspace(event.target.value)}>
                <option value="">選択してください</option>
                {workspaceOptions.map((workspace) => (
                  <option key={workspace.slug} value={workspace.slug}>
                    {workspace.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {message ? <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-[color:var(--danger)]">{message}</div> : null}
          <textarea
            data-testid="ai-assistant-prompt"
            className="field-textarea min-h-28 rounded-md text-sm"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder={embedded ? "今日の予定を教えてください" : "今日の私の予定は？ この資料を要約して。未読メッセージは？"}
          />
          {!embedded ? <div data-testid="ai-action-types" className="grid gap-1 rounded-md border border-[color:var(--line)] bg-[color:var(--bg-subtle)] p-2 text-[11px] text-[color:var(--muted)]">
            <div className="font-semibold text-[color:var(--fg)]">操作確認</div>
            <div>通常確認: 予定・参加予定・資料・TODO・通知設定・改善要望</div>
            <div>二段階確認: DM送信・チャンネル投稿・権限変更・削除・同期モード変更</div>
          </div> : null}
          <button data-testid="ai-assistant-create" type="button" className="button-primary h-10 rounded-md px-4 text-sm font-semibold" onClick={() => void run()} disabled={loading}>
            {loading ? "回答中" : embedded ? "回答を取得" : "提案を作成"}
          </button>
          <div className="grid gap-2">
            {(embedded ? suggestions.slice(0, 1) : suggestions.slice(0, 4)).map((suggestion) => embedded ? (
              <div data-testid="ai-assistant-answer" key={String(suggestion.id)} className="rounded-md border border-[color:var(--line)] bg-[color:var(--bg-subtle)] p-3">
                <div className="text-sm font-semibold">回答</div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[color:var(--fg)]">{suggestion.body || "回答を取得できませんでした。"}</p>
              </div>
            ) : (
              <div data-testid="ai-assistant-suggestion" key={String(suggestion.id)} className="rounded-md border border-[color:var(--line)] bg-[color:var(--bg-subtle)] p-3">
                <div className="text-sm font-semibold">{suggestion.title ?? "AI提案"}</div>
                <p className="mt-1 whitespace-pre-wrap text-xs leading-5 text-[color:var(--muted)]">{suggestion.body ?? ""}</p>
                <div data-testid="ai-action-preview" className="mt-2 rounded-md border border-[color:var(--line)] bg-white px-2 py-2 text-xs text-[color:var(--muted)]">
                  preview: {previewAction(suggestion)} / workspace: {selectedWorkspace}
                </div>
                <div className="mt-2 text-xs text-[color:var(--muted)]">status: {suggestion.status ?? "pending"}</div>
                {suggestion.status === "pending" ? (
                  <div className="mt-3 flex gap-2">
                    <button data-testid="ai-assistant-approve" type="button" className="button-primary h-8 rounded-md px-3 text-xs" onClick={() => void decide(suggestion.id, "approve")}>
                      approve
                    </button>
                    <button data-testid="ai-assistant-reject" type="button" className="button-secondary h-8 rounded-md px-3 text-xs" onClick={() => void decide(suggestion.id, "reject")}>
                      reject
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </aside>
      ) : null}
      {!embedded ? <button
        data-testid="ai-floating-button"
        type="button"
        className="button-primary h-14 w-14 rounded-full text-sm font-semibold shadow-[0_16px_40px_rgba(185,28,28,0.28)]"
        onClick={() => setOpen((current) => !current)}
        aria-label="AI assistant"
      >
        AI
      </button> : null}
    </div>
  );
}

function buildContextSummary(input: {
  assistantContext: AssistantContext;
  workspaceSlug: string;
  workspaceOptions: Array<{ slug: string; name: string }>;
}) {
  const allSummary = input.assistantContext.scope === "all" ? input.workspaceOptions.map((workspace) => `${workspace.name}(${workspace.slug})`).join(", ") : input.workspaceSlug;
  return [
    `route=${input.assistantContext.pathname}`,
    `scope=${input.assistantContext.scope}`,
    `section=${input.assistantContext.section}`,
    `target_workspace=${input.workspaceSlug}`,
    `user=${input.assistantContext.user.name}`,
    `search_params=${JSON.stringify(input.assistantContext.searchParams)}`,
    `visible_workspaces=${allSummary}`,
  ].join("\n");
}

function previewAction(suggestion: Suggestion) {
  if (!suggestion.payload_json) return "内容を確認して承認後に反映";
  try {
    const payload = JSON.parse(suggestion.payload_json) as { materialize?: { action?: string; title?: string } };
    return [payload.materialize?.action, payload.materialize?.title].filter(Boolean).join(" / ") || "内容を確認して承認後に反映";
  } catch {
    return "内容を確認して承認後に反映";
  }
}
